import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, Users, Search, Filter, ChevronRight, Download, X,
  Calendar, CheckSquare, BookOpen, FileSignature, MessageSquare, Video, LogIn, RefreshCw, Edit3, UserCheck
} from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth, ROLE_LABELS, ROLE_COLORS } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";
import RoleBadge from "@/components/RoleBadge";

const ICONS = {
  event: Calendar,
  task: CheckSquare,
  blog: BookOpen,
  loa: FileSignature,
  chat: MessageSquare,
  meeting: Video,
  post: Calendar,
  allotment: UserCheck,
  login: LogIn,
  refresh: RefreshCw,
  edit: Edit3,
};

const KIND_COLORS = {
  event: "bg-orange-100 text-orange-700",
  task: "bg-emerald-100 text-emerald-700",
  blog: "bg-violet-100 text-violet-700",
  loa: "bg-indigo-100 text-indigo-700",
  chat: "bg-slate-100 text-slate-700",
  meeting: "bg-blue-100 text-blue-700",
  post: "bg-pink-100 text-pink-700",
  allotment: "bg-amber-100 text-amber-700",
  login: "bg-slate-100 text-slate-500",
};

export default function ReportsPage() {
  const { user, customRole } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [search, setSearch] = useState("");
  const [activeUser, setActiveUser] = useState(null);
  const [activity, setActivity] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState({ start: "", end: "", kinds: new Set() });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") return;
    (async () => {
      try {
        const [r, cr] = await Promise.all([
          api.get("/admin/reports/users"),
          api.get("/admin/custom-roles").catch(() => ({ data: [] })),
        ]);
        setUsers(r.data || []);
        setCustomRoles(cr.data || []);
      } catch (e) { toast.error("Failed to load users"); }
    })();
  }, [user]);

  useEffect(() => {
    if (!activeUser) return;
    (async () => {
      setBusy(true);
      try {
        const params = {};
        if (filters.start) params.start = filters.start;
        if (filters.end) params.end = filters.end;
        if (filters.kinds.size) params.kinds = Array.from(filters.kinds).join(",");
        const r = await api.get(`/admin/reports/activity/${activeUser.user_id}`, { params });
        setActivity(r.data);
      } catch (e) { toast.error("Failed to load activity"); }
      finally { setBusy(false); }
    })();
  }, [activeUser, filters]);

  const grouped = useMemo(() => {
    const out = {};
    for (const u of users) {
      if (search && !`${u.name} ${u.email}`.toLowerCase().includes(search.toLowerCase())) continue;
      const role = u.role || "other";
      if (!out[role]) out[role] = [];
      out[role].push(u);
    }
    return out;
  }, [users, search]);

  const exportCsv = () => {
    if (!activeUser) return;
    const token = localStorage.getItem("munners_token");
    const params = new URLSearchParams();
    if (filters.start) params.set("start", filters.start);
    if (filters.end) params.set("end", filters.end);
    if (filters.kinds.size) params.set("kinds", Array.from(filters.kinds).join(","));
    params.set("t", token);
    window.open(`${BACKEND_URL}/api/admin/reports/activity/${activeUser.user_id}/export.csv?${params.toString()}`, "_blank");
  };

  const toggleKind = (k) => {
    setFilters((f) => {
      const ks = new Set(f.kinds);
      if (ks.has(k)) ks.delete(k); else ks.add(k);
      return { ...f, kinds: ks };
    });
  };

  if (user?.role !== "admin") {
    return (
      <Layout navItems={getNav(user.role, customRole)}>
        <div className="p-12 text-center text-slate-400">Reports are admin-only.</div>
      </Layout>
    );
  }

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="reports-page">
        <PageHeader
          title="Reports"
          subtitle="Click any user to see their complete activity timeline across the platform."
          icon={Activity}
          accent="orange"
          actions={
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input className="input-pill text-sm w-56" style={{ paddingLeft: "2.5rem" }} placeholder="Search by name/email…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="reports-search" />
              </div>
            </div>
          }
        />

        <div className="grid lg:grid-cols-3 gap-5">
          {/* User list */}
          <div className="lg:col-span-1 space-y-3">
            {Object.keys(grouped).sort().map((role) => (
              <div key={role} className="dashboard-card p-3" data-testid={`role-group-${role}`}>
                <div className="mb-2"><RoleBadge role={role} customRoles={customRoles} /></div>
                <div className="space-y-1">
                  {grouped[role].map((u) => (
                    <button key={u.user_id} onClick={() => setActiveUser(u)}
                      className={`w-full flex items-center gap-2 p-2 rounded-xl text-left transition-all ${activeUser?.user_id === u.user_id ? "bg-orange-50 border border-orange-200" : "hover:bg-slate-50 border border-transparent"}`}
                      data-testid={`reports-user-${u.user_id}`}>
                      <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-orange-400 to-amber-300 flex items-center justify-center text-white font-bold text-xs overflow-hidden flex-shrink-0">
                        {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> : u.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{u.name}</div>
                        <div className="text-[10px] text-slate-400 truncate">{u.email}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {users.length === 0 && (
              <div className="dashboard-card p-8 text-center text-slate-400 text-sm">No non-admin users yet.</div>
            )}
          </div>

          {/* Timeline panel */}
          <div className="lg:col-span-2 space-y-3">
            {!activeUser ? (
              <div className="dashboard-card p-16 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <div className="font-display font-bold text-slate-400">Pick a user to view their timeline</div>
                <p className="text-xs text-slate-400 mt-2">You'll see logins, events, tasks, blogs, LOAs, chat, meetings, posts and allotments in chronological order.</p>
              </div>
            ) : (
              <>
                {/* User card */}
                <div className="dashboard-card p-5 bg-gradient-to-r from-indigo-50/40 to-fuchsia-50/40">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center text-white font-display font-black text-2xl overflow-hidden">
                      {activeUser.picture ? <img src={activeUser.picture} alt="" className="w-full h-full object-cover" /> : activeUser.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-black text-xl truncate">{activeUser.name}</div>
                      <div className="text-xs text-slate-500 truncate">{activeUser.email}</div>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <RoleBadge role={activeUser.role} customRoles={customRoles} />
                        {activeUser.member_id && <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-full font-bold">ID: {activeUser.member_id}</span>}
                        {activeUser.city && <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">{activeUser.city}</span>}
                        {activeUser.region && <span className="text-[10px] bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-bold">{activeUser.region}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-display font-black text-orange-600" data-testid="activity-total">{activity.total}</div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">activities</div>
                    </div>
                  </div>
                </div>

                {/* Filters */}
                <div className="dashboard-card p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <Filter className="w-4 h-4 text-slate-400" />
                    <input type="date" className="input-pill text-xs" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} data-testid="reports-filter-start" />
                    <span className="text-xs text-slate-400">→</span>
                    <input type="date" className="input-pill text-xs" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} data-testid="reports-filter-end" />
                    <button onClick={() => setFilters({ start: "", end: "", kinds: new Set() })} className="btn-ghost text-xs">Clear</button>
                    <div className="flex-1" />
                    <button onClick={exportCsv} className="btn-ghost text-xs" data-testid="reports-export-csv"><Download className="w-3.5 h-3.5" /> CSV</button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {Object.keys(ICONS).filter((k) => k !== "edit" && k !== "refresh").map((k) => {
                      const active = filters.kinds.has(k);
                      const Icon = ICONS[k];
                      return (
                        <button key={k} onClick={() => toggleKind(k)} className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide transition-all ${active ? KIND_COLORS[k] : "bg-slate-50 text-slate-400 hover:bg-slate-100"}`} data-testid={`reports-kind-${k}`}>
                          <Icon className="w-3 h-3" /> {k}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Timeline */}
                <div className="dashboard-card p-5 max-h-[60vh] overflow-y-auto">
                  {busy ? <div className="text-slate-400 text-center py-8">Loading activity…</div>
                    : activity.items.length === 0 ? <div className="text-slate-400 text-center py-8">No activity in this range.</div>
                    : (
                      <div className="space-y-2">
                        {activity.items.map((it, i) => {
                          const Icon = ICONS[it.icon] || ICONS[it.kind] || Activity;
                          return (
                            <div key={i} className={`flex items-start gap-3 p-2.5 rounded-xl hover:bg-slate-50 border-l-4 ${KIND_COLORS[it.kind]?.replace("bg-", "border-").replace("text-", "") || "border-slate-200"}`} data-testid={`timeline-item-${i}`}>
                              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${KIND_COLORS[it.kind] || "bg-slate-100 text-slate-500"}`}>
                                <Icon className="w-3.5 h-3.5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-slate-800 truncate">{it.title}</div>
                                <div className="text-[10px] text-slate-400">{it.at ? new Date(it.at).toLocaleString() : "—"}</div>
                              </div>
                              <span className={`role-pill ${KIND_COLORS[it.kind] || "bg-slate-100"}`}>{it.kind}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
